import { Checkbox } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { SearchResultEnriched } from 'lib-search-types';
import type { Tag } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { MouseEvent, useCallback } from 'react';
import { Writable } from 'ts-essentials';

import { getMetaConversationName } from 'app/utils/conversation';
import { ConversationDetails } from 'components/conversation/conversationDetails';
import { useIsRestrictedFromQueue } from 'utils/user/useUserQueues';

import searchResultStyles from '../../../../../searchResults.module.scss';
import { SearchResult } from '../../../../result/searchResult';

import styles from './conversationResult.module.scss';
import { convertSearchConversationToMetaConversation } from './utils/convertSearchConversationToMetaConversation';

type ConversationResultT = SearchResultEnriched<'conversations'>;

type ConversationResultProps = {
  result: ConversationResultT;
  isSelectable: boolean;
  isSelected: boolean;
  onRowSelect: (result: ConversationResultT) => void;
  onRowClick: (result: ConversationResultT) => void;
} & PropsWithClassName;

/**
 * Represents a component that is used to render the conversation search result
 * item
 */
export const ConversationResult = ({
  className,
  result,
  isSelectable,
  isSelected,
  onRowSelect,
  onRowClick,
}: ConversationResultProps): JSX.Element => {
  const isEventTargetExcluded = useCallback(
    ({ target }: MouseEvent<HTMLDivElement>) => {
      const targetEl = target as Element;
      return (
        targetEl.tagName === 'LABEL' || targetEl.matches('[type="checkbox"]')
      );
    },
    [],
  );
  const metaConversation = convertSearchConversationToMetaConversation(
    result.result,
  );
  const isRestricted = useIsRestrictedFromQueue(
    metaConversation.conversation.queue || null,
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (
        isRestricted === undefined ||
        isRestricted ||
        isEventTargetExcluded(event)
      ) {
        return;
      }
      onRowClick(result);
    },
    [result, isEventTargetExcluded, onRowClick, isRestricted],
  );

  const title = getMetaConversationName(metaConversation);

  return (
    <SearchResult
      className={classNames(className, {
        [searchResultStyles.rowClickable]: isRestricted === false,
      })}
      onClick={handleClick}
      content={
        <div className={styles.conversationSummaryWrapper}>
          {isSelectable && (
            <Checkbox
              onChange={() => onRowSelect(result)}
              checked={isSelected}
              id={`${result.result.id}`}
              name={`${result.result.id}`}
            />
          )}

          <ConversationDetails
            metaConversation={metaConversation}
            conversationName={title}
            showMessageCount
            showTags
            tags={(result.result.tags ?? []) as Writable<Tag>[]}
            conversationUrl={`/conversation/${result.result.id}`}
            linkToConversation={false}
          />
        </div>
      }
    />
  );
};
